import {
  addDays,
  endOfDay,
  startOfDay,
  startOfMonth,
  endOfMonth,
  addMonths,
  startOfWeek,
  endOfWeek,
  isSameDay,
  subDays,
} from 'date-fns'
import moment from 'moment'
import React from 'react'

// Read the clock when a preset is picked, not when this module is imported.
// As a frozen object these boundaries were pinned to page-load time, so a tab
// left open past midnight kept offering yesterday as "Today" — and any test
// that sets a system time after import got the real day instead of its own.
const currentDateRange = () => ({
  startOfWeek: startOfWeek(new Date()),
  endOfWeek: endOfWeek(new Date()),
  startOfLastWeek: startOfWeek(addDays(new Date(), -7)),
  endOfLastWeek: endOfWeek(addDays(new Date(), -7)),
  startOfToday: startOfDay(new Date()),
  endOfToday: endOfDay(new Date()),
  startOfMonth: startOfMonth(new Date()),
  endOfMonth: endOfMonth(new Date()),
  startOfLastMonth: startOfMonth(addMonths(new Date(), -1)),
  endOfLastMonth: endOfMonth(addMonths(new Date(), -1)),
  startOfSixDaysAgo: subDays(new Date(), 6),
  startOfTwoMonthsAgo: subDays(new Date(), 59),
  startOfOneMonthAgo: subDays(new Date(), 29),
  startOfFourMonthsAgo: subDays(new Date(), 119),
})

const staticRangeHandler = {
  range: {},
  isSelected(range) {
    const definedRange = this.range()
    const sameDays =
      isSameDay(range.startDate, definedRange.startDate) &&
      isSameDay(range.endDate, definedRange.endDate)
    // Presets can cover the same days (30 Sep: This Month and Last 30 Days), so only the one the picker names is lit.
    return sameDays && (!range.presetLabel || range.presetLabel === this.label)
  },
}

// "the past week" is default selection so isn't listed as an option
export const customStaticRanges = [
  {
    label: 'Today',
    range: () => {
      const dateRange = currentDateRange()
      return {
        text: 'today',
        startDate: dateRange.startOfToday,
        endDate: dateRange.endOfToday,
      }
    },
  },
  {
    label: 'This Week',
    range: () => {
      const dateRange = currentDateRange()
      return {
        text: 'this_week',
        startDate: dateRange.startOfWeek,
        endDate: dateRange.startOfToday,
      }
    },
  },
  {
    label: 'Last Week',
    range: () => {
      const dateRange = currentDateRange()
      return {
        text: 'last_week',
        startDate: dateRange.startOfLastWeek,
        endDate: dateRange.endOfLastWeek,
      }
    },
  },
  {
    label: 'This Month',
    range: () => {
      const dateRange = currentDateRange()
      return {
        text: 'this_month',
        startDate: dateRange.startOfMonth,
        endDate: dateRange.endOfMonth,
      }
    },
  },
  {
    label: 'Last Month',
    range: () => {
      const dateRange = currentDateRange()
      return {
        text: 'last_month',
        startDate: dateRange.startOfLastMonth,
        endDate: dateRange.endOfLastMonth,
      }
    },
  },
  {
    label: 'Last 7 Days',
    range: () => {
      const dateRange = currentDateRange()
      return {
        text: 'last_7_days',
        startDate: dateRange.startOfSixDaysAgo,
        endDate: dateRange.endOfToday,
      }
    },
  },
  {
    label: 'Last 30 Days',
    range: () => {
      const dateRange = currentDateRange()
      return {
        text: 'last_30_days',
        startDate: dateRange.startOfOneMonthAgo,
        endDate: dateRange.endOfToday,
      }
    },
  },
  {
    label: 'Last 60 Days',
    range: () => {
      const dateRange = currentDateRange()
      return {
        text: 'last_60_days',
        startDate: dateRange.startOfTwoMonthsAgo,
        endDate: dateRange.endOfToday,
      }
    },
  },
].map((range) => ({ ...staticRangeHandler, ...range }))

export const customStaticRanges120 = [
  ...customStaticRanges,
  {
    ...staticRangeHandler,
    label: 'Last 120 Days',
    range: () => {
      const dateRange = currentDateRange()
      return {
        text: 'last_120_days',
        startDate: dateRange.startOfFourMonthsAgo,
        endDate: dateRange.endOfToday,
      }
    },
  },
]

// dateRange is the picked preset's saved-filter key ('last 30 days'): a tie-break between presets with the same days.
export const getDateRangeLabel = ({ startDate, endDate, dateRange }) => {
  const matches = customStaticRanges120.filter((staticRange) => {
    const range = staticRange.range()

    return (
      moment(startDate).format('L') === moment(range.startDate).format('L') &&
      moment(endDate).format('L') === moment(range.endDate).format('L')
    )
  })
  const match = matches.find(({ label }) => label.toLowerCase() === dateRange) || matches[0]

  return match?.label || null
}

export const getDateRangeKey = (text) =>
  customStaticRanges120
    .find((staticRange) => staticRange.range().text === text)
    ?.label.toLowerCase()

export const getTriggerLabel = (startDate, endDate, basicLabel, friendlyLabel, dateRange) => {
  if (!startDate && !endDate) {
    return 'Select Date'
  }

  if (startDate && (!endDate || endDate === 'Invalid date')) {
    return `${moment(startDate).format('L')} to End Date`
  }

  if ((!startDate || startDate === 'Invalid date') && endDate) {
    return `Start Date to ${moment(endDate).format('L')}`
  }

  const formattedStartDate = moment(startDate).format('L')
  const formattedEndDate = moment(endDate).format('L')
  let displayLabel = `${formattedStartDate} to ${formattedEndDate}`

  if (friendlyLabel) {
    displayLabel = getDateRangeLabel({ startDate, endDate, dateRange }) || displayLabel
  }

  return basicLabel ? (
    displayLabel
  ) : (
    <div className="date-range-display" data-testid="default-label">
      <strong>{formattedStartDate}</strong>
      <span>to</span>
      <strong>{formattedEndDate}</strong>
    </div>
  )
}

export const isValidDate = (date) => {
  // eslint-disable-next-line no-restricted-globals
  return !isNaN(date)
}

export const determineGradient = (gradientNumber) => `var(--gradient-${gradientNumber % 20})`

export const dateAriaLabels = {
  dateInput: {
    selectedRange: {
      startDate: 'start date input of selected range',
      endDate: 'end date input of selected range',
    },
  },
  monthPicker: 'month picker',
  yearPicker: 'year picker',
  prevButton: 'previous month button',
  nextButton: 'next month button',
}
